import React from 'react';
import { toast } from 'sonner';
import {
  Twitter, Facebook, MessageCircle, Mail, Link2, Music2, Send, Camera,
} from 'lucide-react';

/**
 * ShareBar — universal social-share toolbar used across the site.
 *
 * Platforms (ordered by likely-engagement for the gallery audience):
 *   Twitter/X, Facebook, WhatsApp, Pinterest, Telegram,
 *   Instagram (copy + open), TikTok (copy + open), Discord (copy + open),
 *   Email, Copy link.
 *
 * Instagram / TikTok / Discord don't expose web-share URLs. For these we
 * copy the canonical link to clipboard and then deep-link to the platform
 * so the user can paste it into a Story / DM / channel.
 *
 * Props:
 *   url      — canonical URL to share (must be absolute, e.g. https://...)
 *   title    — short share text (post title, artwork title, etc.)
 *   image    — image URL for platforms that support it (Pinterest)
 *   compact  — render a tighter row (icon-only) when used in tight spots
 */
const _enc = (s) => encodeURIComponent(s ?? '');

const ShareBar = ({ url, title, image, compact = false, className = '' }) => {
  const safeTitle = title || 'Zebra One Gallery';
  const text = `${safeTitle} — ${url}`;

  const copyAnd = async (after) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied — now paste it into the post');
    } catch (err) {
      // Fallback for browsers without clipboard API
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        toast.success('Link copied — now paste it into the post');
      } catch (innerErr) {
        toast.error('Could not copy. Please copy the URL from the address bar.');
      }
      document.body.removeChild(ta);
    }
    if (after) window.open(after, '_blank', 'noopener,noreferrer');
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
    } catch (err) {
      toast.error('Could not copy. Please copy the URL from the address bar.');
    }
  };

  const links = [
    {
      key: 'twitter',
      label: 'X / Twitter',
      icon: Twitter,
      href: `https://twitter.com/intent/tweet?text=${_enc(safeTitle)}&url=${_enc(url)}`,
      external: true,
    },
    {
      key: 'facebook',
      label: 'Facebook',
      icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${_enc(url)}`,
      external: true,
    },
    {
      key: 'whatsapp',
      label: 'WhatsApp',
      icon: MessageCircle,
      href: `https://api.whatsapp.com/send?text=${_enc(text)}`,
      external: true,
    },
    {
      key: 'pinterest',
      label: 'Pinterest',
      icon: Camera,
      // Pinterest requires a media (image) URL; falls back to plain link if absent
      href: image
        ? `https://pinterest.com/pin/create/button/?url=${_enc(url)}&description=${_enc(safeTitle)}&media=${_enc(image)}`
        : `https://pinterest.com/pin/create/button/?url=${_enc(url)}&description=${_enc(safeTitle)}`,
      external: true,
    },
    {
      key: 'telegram',
      label: 'Telegram',
      icon: Send,
      href: `https://t.me/share/url?url=${_enc(url)}&text=${_enc(safeTitle)}`,
      external: true,
    },
    {
      key: 'instagram',
      label: 'Instagram',
      icon: Camera,
      onClick: () => copyAnd('https://www.instagram.com/'),
    },
    {
      key: 'tiktok',
      label: 'TikTok',
      icon: Music2,
      onClick: () => copyAnd('https://www.tiktok.com/'),
    },
    {
      key: 'discord',
      label: 'Discord',
      icon: MessageCircle,
      onClick: () => copyAnd('https://discord.com/channels/@me'),
    },
    {
      key: 'email',
      label: 'Email',
      icon: Mail,
      href: `mailto:?subject=${_enc(safeTitle)}&body=${_enc(`Thought you might find this interesting:\n\n${url}`)}`,
    },
    {
      key: 'copy',
      label: 'Copy link',
      icon: Link2,
      onClick: copyLink,
    },
  ];

  const baseBtn = compact
    ? 'inline-flex items-center justify-center w-9 h-9 border border-border hover:border-foreground hover:bg-foreground hover:text-background transition-colors'
    : 'inline-flex items-center gap-2 px-3 py-2 border border-border hover:border-foreground hover:bg-foreground hover:text-background transition-colors text-sm';

  return (
    <div
      className={`flex flex-wrap items-center gap-2 ${className}`}
      data-testid="share-bar"
      role="group"
      aria-label="Share this page"
    >
      {!compact && (
        <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground mr-2">
          Share
        </span>
      )}
      {links.map(({ key, label, icon: Icon, href, external, onClick }) => {
        const content = (
          <>
            <Icon className="w-4 h-4" />
            {!compact && <span>{label}</span>}
          </>
        );
        const common = {
          'data-testid': `share-${key}`,
          title: label,
          'aria-label': `Share on ${label}`,
          className: baseBtn,
        };
        if (href) {
          return (
            <a
              key={key}
              href={href}
              target={external ? '_blank' : undefined}
              rel={external ? 'noopener noreferrer' : undefined}
              {...common}
            >
              {content}
            </a>
          );
        }
        return (
          <button key={key} type="button" onClick={onClick} {...common}>
            {content}
          </button>
        );
      })}
    </div>
  );
};

export default ShareBar;
